import React, {
  createContext,
  Dispatch,
  useMemo,
  ReactNode,
  useContext,
  useReducer,
} from "react";
import {
  AuthCheckResult,
  AuthUserDetails,
  GarminAccountDetails,
  authCheck,
} from "./authCheck";

interface AuthState {
  isAuthenticated: boolean;
  isConnected: boolean;
  isAdmin: boolean;
  error: string | null;
  user: AuthUserDetails | null;
  account: GarminAccountDetails | null;
}

type AuthAction =
  | {
      type: "CHECK_SUCCESS";
      authenticated: boolean;
      connected: boolean;
      admin: boolean;
      user: AuthUserDetails | null;
      account: GarminAccountDetails | null;
    }
  | { type: "CHECK_FAILURE"; error: string }
  | { type: "RESET" };

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case "CHECK_SUCCESS":
      return {
        isAuthenticated: action.authenticated,
        isConnected: action.connected,
        isAdmin: action.admin,
        error: null,
        user: action.user,
        account: action.account,
      };
    case "CHECK_FAILURE":
      return {
        isAuthenticated: false,
        isConnected: false,
        isAdmin: false,
        error: action.error,
        user: null,
        account: null,
      };
    case "RESET":
      return {
        isAuthenticated: false,
        isConnected: false,
        isAdmin: false,
        error: null,
        user: null,
        account: null,
      };
    default:
      return state;
  }
}

const initialState: AuthState = {
  isAuthenticated: false,
  isConnected: false,
  isAdmin: false,
  error: null,
  user: null,
  account: null,
};

const actionsCreator = (dispatch: Dispatch<AuthAction>) => ({
  checkAuth: async (): Promise<AuthCheckResult> => {
    const result = await authCheck();

    if (!result.ok) {
      dispatch({
        type: "CHECK_FAILURE",
        error: result.error || "Unable to verify your LaceAI session.",
      });
      return result;
    }

    dispatch({
      type: "CHECK_SUCCESS",
      authenticated: result.authenticated,
      connected: result.connected,
      admin: result.admin,
      user: result.user ?? null,
      account: result.account ?? null,
    });

    return result;
  },
});

type AuthActions = ReturnType<typeof actionsCreator>;

interface StoreContextValue {
  state: AuthState;
  actions: AuthActions;
}

const AuthStoreContext = createContext<StoreContextValue>({
  state: initialState,
  actions: {} as AuthActions,
});

export const AuthStoreProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const actions = useMemo(() => actionsCreator(dispatch), [dispatch]);

  return (
    <AuthStoreContext.Provider value={{ actions, state }}>
      {children}
    </AuthStoreContext.Provider>
  );
};

// Hook to consume the store
export function useAuthStore(): StoreContextValue {
  const ctx = useContext(AuthStoreContext);
  if (!ctx) {
    throw new Error("useStore must be used inside a StoreProvider");
  }
  return ctx;
}
